/**
 * BPMN Editor Panel
 *
 * Visual BPMN editor using bpmn-js for editing workflow processes.
 * This is the PRIMARY visual editing interface for skills.
 * Edits to BPMN can be used to regenerate LumaJSON.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import BpmnModeler from 'bpmn-js/lib/Modeler';
import 'bpmn-js/dist/assets/diagram-js.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css';

import { useProjectStore } from '@/stores/projectStore';
import { useToastStore } from '@/stores/toastStore';
import { Button } from '../ui/Button';
import { Save, RefreshCw, Download, Loader2, AlertCircle, Sparkles } from 'lucide-react';
import { transformApi } from '@/lib/api';
import { buildUserHeaders } from '@/stores/userStore';
import { API_BASE_URL } from '@/lib/config';

// Empty BPMN template
const EMPTY_BPMN_TEMPLATE = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL"
                  xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
                  xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"
                  id="Definitions_1"
                  targetNamespace="http://bpmn.io/schema/bpmn">
  <bpmn:process id="Process_1" isExecutable="false">
    <bpmn:startEvent id="StartEvent_1" />
  </bpmn:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="Process_1" />
  </bpmndi:BPMNDiagram>
</bpmn:definitions>`;

export const BPMNEditorPanel: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const modelerRef = useRef<BpmnModeler | null>(null);

  const activeProjectId = useProjectStore((state) => state.activeProjectId);
  const selectedArtifactId = useProjectStore((state) => state.ui.selectedArtifactId);
  const projects = useProjectStore((state) => state.projects);
  const updateArtifactStage = useProjectStore((state) => state.updateArtifactStage);
  const setActiveStage = useProjectStore((state) => state.setActiveStage);
  const { addToast } = useToastStore();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Get current project and artifact
  const project = activeProjectId ? projects[activeProjectId] : null;
  const artifact = project && selectedArtifactId ? project.artifacts[selectedArtifactId] : null;
  const bpmnXml = artifact?.stages?.BPMN?.content || null;
  const lumascript = artifact?.stages?.LUMASCRIPT?.content || null;

  // Initialize BPMN modeler
  useEffect(() => {
    if (!containerRef.current) return;

    const modeler = new BpmnModeler({
      container: containerRef.current,
      keyboard: {
        bindTo: document
      }
    });

    modelerRef.current = modeler;

    // Listen for changes to mark as dirty
    modeler.on('commandStack.changed', () => {
      setIsDirty(true);
    });

    return () => {
      modeler.destroy();
    };
  }, []);

  // Load BPMN XML when artifact changes
  useEffect(() => {
    if (!modelerRef.current) return;

    setIsLoading(true);
    setError(null);

    // Use existing BPMN or empty template
    const xmlToLoad = bpmnXml || EMPTY_BPMN_TEMPLATE;

    modelerRef.current
      .importXML(xmlToLoad)
      .then(() => {
        setIsLoading(false);
        setIsDirty(false);

        // Fit to viewport
        const canvas = modelerRef.current!.get('canvas') as any;
        canvas.zoom('fit-viewport');
      })
      .catch((err: Error) => {
        console.error('[BPMNEditor] Failed to load BPMN:', err);
        setError(`Failed to load BPMN: ${err.message}`);
        setIsLoading(false);
      });
  }, [bpmnXml, artifact?.id]);

  // Save BPMN
  const handleSave = useCallback(async () => {
    if (!modelerRef.current || !artifact) return;

    setIsSaving(true);
    try {
      const { xml } = await modelerRef.current.saveXML({ format: true });

      if (!xml) {
        throw new Error('Failed to export BPMN XML');
      }

      await updateArtifactStage(artifact.id, 'BPMN', xml, false);
      setIsDirty(false);
      addToast('success', 'BPMN saved successfully');
    } catch (err: any) {
      console.error('[BPMNEditor] Save failed:', err);
      addToast('error', `Failed to save BPMN: ${err.message || 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  }, [artifact, updateArtifactStage, addToast]);

  // Regenerate BPMN from LumaScript
  const handleRegenerateFromLumaScript = useCallback(async () => {
    if (!artifact || !lumascript) {
      addToast('error', 'LumaScript not found. Generate LumaScript first.');
      return;
    }

    if (isDirty) {
      const confirmDiscard = window.confirm(
        'You have unsaved changes. Regenerating will discard them. Continue?'
      );
      if (!confirmDiscard) return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const lumascriptObj = JSON.parse(lumascript);

      // Call lumascript-to-bpmn API
      const response = await fetch(`${API_BASE_URL}/transforms/lumascript-to-bpmn`, {
        method: 'POST',
        headers: buildUserHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          lumascript: lumascriptObj,
          artifactId: artifact.id,
          projectId: activeProjectId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to generate BPMN');
      }

      // For local dev, BPMN is returned immediately
      if (data.status === 'completed') {
        addToast('success', 'BPMN regenerated successfully');
        // The BPMN will be loaded via the stage update (polling or direct)
        // For now, let's fetch the updated artifact
        window.location.reload(); // Simple refresh - in production would use proper state update
        return;
      }

      // For production, poll for job completion
      const jobId = data.jobId;
      if (jobId) {
        addToast('info', 'BPMN generation started...');
        // Poll for completion
        const pollInterval = setInterval(async () => {
          try {
            const statusResp = await fetch(`${API_BASE_URL}/jobs/${jobId}`, {
              headers: buildUserHeaders()
            });
            const job = await statusResp.json();

            if (job.status === 'completed') {
              clearInterval(pollInterval);
              addToast('success', 'BPMN regenerated successfully');
              window.location.reload();
            } else if (job.status === 'failed') {
              clearInterval(pollInterval);
              addToast('error', `BPMN generation failed: ${job.error || 'Unknown error'}`);
              setIsGenerating(false);
            }
          } catch (pollErr: any) {
            clearInterval(pollInterval);
            addToast('error', 'Failed to poll job status');
            setIsGenerating(false);
          }
        }, 2000);
      }
    } catch (err: any) {
      console.error('[BPMNEditor] Regenerate failed:', err);
      addToast('error', `Failed to regenerate BPMN: ${err.message || 'Unknown error'}`);
      setIsGenerating(false);
    }
  }, [artifact, lumascript, isDirty, activeProjectId, addToast]);

  // Export BPMN XML
  const handleExport = useCallback(async () => {
    if (!modelerRef.current || !artifact) return;

    try {
      const { xml } = await modelerRef.current.saveXML({ format: true });

      if (!xml) {
        throw new Error('Failed to export BPMN XML');
      }

      // Create download
      const blob = new Blob([xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${artifact.identifier || 'workflow'}_bpmn.xml`;
      a.click();
      URL.revokeObjectURL(url);

      addToast('success', 'BPMN exported successfully');
    } catch (err: any) {
      console.error('[BPMNEditor] Export failed:', err);
      addToast('error', `Failed to export BPMN: ${err.message || 'Unknown error'}`);
    }
  }, [artifact, addToast]);

  // Navigate to LumaJSON Viewer
  const handleToLumaJSON = () => {
    if (isDirty) {
      const confirmNavigate = window.confirm(
        'You have unsaved changes. Save before navigating?'
      );
      if (confirmNavigate) {
        handleSave().then(() => {
          setActiveStage('LUMAJSON_VIEWER');
        });
        return;
      }
    }
    setActiveStage('LUMAJSON_VIEWER');
  };

  // No project selected
  if (!project) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-50">
        <div className="text-center text-muted-foreground">
          <p className="text-sm">No project selected. Create or open a project to get started.</p>
        </div>
      </div>
    );
  }

  // No artifact selected
  if (!artifact) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-50">
        <div className="text-center text-muted-foreground">
          <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-sm mb-2 font-medium">No Artifact Selected</p>
          <p className="text-xs text-gray-500">
            Select an artifact from the Explorer to edit its BPMN workflow.
          </p>
        </div>
      </div>
    );
  }

  // No LumaScript yet
  if (!lumascript) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-50">
        <div className="text-center text-muted-foreground max-w-md">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
          <p className="text-sm mb-2 font-medium">LumaScript Required</p>
          <p className="text-xs text-gray-500 mb-4">
            Generate LumaScript first before editing BPMN. The BPMN will be automatically generated from your LumaScript.
          </p>
          <Button
            size="sm"
            onClick={() => setActiveStage('LUMASCRIPT')}
          >
            Go to LumaScript
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* Toolbar */}
      <div className="h-12 border-b border-border bg-background flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium">{artifact.name}</span>
          {isDirty && (
            <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">
              Unsaved Changes
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={handleRegenerateFromLumaScript}
            disabled={isGenerating || !lumascript}
            className="flex items-center gap-2"
            title="Regenerate BPMN from LumaScript"
          >
            {isGenerating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Regenerate from LumaScript
          </Button>

          <Button
            size="sm"
            variant="secondary"
            onClick={handleExport}
            className="flex items-center gap-2"
            title="Export BPMN XML"
          >
            <Download className="w-4 h-4" />
            Export XML
          </Button>

          <Button
            size="sm"
            variant="primary"
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            className="flex items-center gap-2"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save
          </Button>

          <button
            onClick={handleToLumaJSON}
            className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-orange-500/20 rounded transition-colors"
          >
            To LumaJSON Viewer →
          </button>
        </div>
      </div>

      {/* BPMN Canvas */}
      <div className="flex-1 relative bg-white" style={{ minHeight: 0 }}>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              <p className="text-sm text-muted-foreground">Loading BPMN...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
            <div className="text-center max-w-md">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
              <p className="text-sm font-medium mb-2">Failed to Load BPMN</p>
              <p className="text-xs text-muted-foreground mb-4">{error}</p>
              <Button size="sm" onClick={handleRegenerateFromLumaScript}>
                Regenerate from LumaScript
              </Button>
            </div>
          </div>
        )}

        <div ref={containerRef} className="w-full h-full" />
      </div>
    </div>
  );
};
